/**
 * Which lab files Lattice accepts and how big they may be (ONB-012), shared by the browser's file
 * picker and the Worker that stores them so the two never disagree about what a photo is.
 *
 * Browsers outside Safari often report an empty type for a HEIC photo, and some report
 * application/octet-stream for anything they do not recognise, so a file is judged by its
 * extension when the reported type says nothing.
 */

/** Engineering default. */
export const MAX_LAB_FILE_BYTES = 25 * 1024 * 1024;

const EXTENSIONS = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/heic": [".heic"],
  "image/heif": [".heif"],
  "image/webp": [".webp"],
} as const;

export type LabFileType = keyof typeof EXTENSIONS;

const TYPES = Object.keys(EXTENSIONS).filter((type): type is LabFileType => type in EXTENSIONS);

/** The value for a file input's `accept`, listing types and extensions both. */
export const LAB_FILE_ACCEPT = [...TYPES, ...Object.values(EXTENSIONS).flat()].join(",");

/** The stored type of a lab file, or null when Lattice cannot read it. */
export function labFileType(name: string, reportedType: string): LabFileType | null {
  const known = TYPES.find((type) => type === reportedType);

  if (known) return known;

  if (reportedType !== "" && reportedType !== "application/octet-stream") return null;
  const lower = name.toLowerCase();

  return (
    TYPES.find((type) => EXTENSIONS[type].some((extension) => lower.endsWith(extension))) ?? null
  );
}

/** True for a PDF, whose size problem has a different fix from a photo's. */
export function isPdf(name: string, reportedType: string): boolean {
  return labFileType(name, reportedType) === "application/pdf";
}
